export const OFFLINE_BATCH_LIMIT_MAX_KB = 256;
const OFFLINE_BATCH_LIMIT_DEFAULT_KB = 256;

export function offlineBatchLimitKey(storeId: string) {
  return `offlineBatchSizeKb:${storeId}`;
}

export function normalizeOfflineBatchSizeKb(value: unknown): number {
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > OFFLINE_BATCH_LIMIT_MAX_KB) {
    return OFFLINE_BATCH_LIMIT_DEFAULT_KB;
  }
  return parsed;
}

export function rememberOfflineBatchLimit(storeId: string, value: unknown) {
  if (!storeId || typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(offlineBatchLimitKey(storeId), String(normalizeOfflineBatchSizeKb(value)));
  } catch {
    // The send path uses 256 KB when this station has no saved limit.
  }
}

export function readOfflineBatchLimitBytes(storeId: string): number {
  if (!storeId || typeof localStorage === "undefined") return OFFLINE_BATCH_LIMIT_DEFAULT_KB * 1024;
  try {
    const saved = localStorage.getItem(offlineBatchLimitKey(storeId));
    return normalizeOfflineBatchSizeKb(saved) * 1024;
  } catch {
    return OFFLINE_BATCH_LIMIT_DEFAULT_KB * 1024;
  }
}

export function offlineSizeLabel(bytes: number): string {
  const size = Number(bytes) || 0;
  const kb = size < 1024 ? 1 : Math.round(size / 1024);
  return `${kb}K`;
}

export function offlineBillSizeLabel(bill: unknown): string {
  const bytes = new TextEncoder().encode(JSON.stringify(bill ?? {})).length;
  return offlineSizeLabel(bytes);
}

export function offlineRequestBytes(headers: Record<string, unknown> | null | undefined, reqTrans: unknown): number {
  const encoder = new TextEncoder();
  let size = encoder.encode(JSON.stringify(reqTrans)).length;
  if (!headers) return size;
  Object.entries(headers).forEach(([name, value]) => {
    size += encoder.encode(`${name}: ${value ?? ""}\r\n`).length;
  });
  return size;
}

/** JSON transLot is newest first. Signature bills stay oldest first, with the live bill last. */
export function offlineBatchRequest(live: any, rows: any[]) {
  const chronological = rows.map((row) => row?.reqTrans?.transActu);
  const transLot = [...chronological].reverse();
  const reqTrans = live
    ? (transLot.length ? { transActu: live, transLot } : { transActu: live })
    : { transLot };
  const signatureBills = live ? [...chronological, live] : chronological;
  return { transLot, reqTrans, signatureBills };
}

/** Oldest queued rows that fit with the live bill. A lone bill that is already over the limit is returned alone. */
export function oldestRowsWithinLimit(rows: any[], live: any, limitBytes: number, headerBytes: number): any[] {
  const fits = (nextRows: any[]) => (
    offlineRequestBytes(null, offlineBatchRequest(live, nextRows).reqTrans) + headerBytes <= limitBytes
  );
  if (live && !fits([])) return [];
  const chosen: any[] = [];
  for (const row of rows) {
    if (!fits([...chosen, row])) {
      if (!live && chosen.length === 0) return [row];
      break;
    }
    chosen.push(row);
  }
  return chosen;
}
